import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  Tooltip,
  Checkbox,
  styled,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { AmbienteExterno, TipoAmbienteExterno } from '../types/negocio/AmbienteExterno';
import { ambienteExternoService } from '../services/AmbienteExternoService';
import toastService from '../utils/toastService';
import SaveStatusIndicator, { SaveStatus } from '../components/SaveStatusIndicator';
import LoadingIndicator from './LoadingIndicator';
import { CLASSIFICACOES_AMBIENTE_EXTERNO } from '../types/constants/ClassificacaoAmbienteExterno';
import InfoPopover from './InfoPopover';
import PrimaryButton from './PrimaryButton';
import ConfirmDeleteDialog from './modais/ConfirmDeleteDialog';
import ProtecaoBotao from './ProtecaoBotao';
import { useAcesso } from '../hooks/useAcesso';
import ProtectedBasicMenu from './ProtectedBasicMenu';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#042E4B',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const impactoOptions = [
  { value: 4, label: "(4) Alto" },
  { value: 3, label: "(3) Médio" },
  { value: 2, label: "(2) Baixo" },
  { value: 1, label: "(1) Nenhum" },
];

const probabilidadeOptions = [
  { value: 4, label: "(4) Grande" },
  { value: 3, label: "(3) Média" },
  { value: 2, label: "(2) Baixa" },
  { value: 1, label: "(1) Nenhuma" },
];

const tendenciaOptions = [
  { value: 4, label: "(4) Ampliar" },
  { value: 3, label: "(3) Estabilizar" },
  { value: 2, label: "(2) Reduzir" },
  { value: 1, label: "(1) Zerar" },
];

interface TabelaPESTELProps {
  tipo: TipoAmbienteExterno;
}

const TabelaPESTEL: React.FC<TabelaPESTELProps> = (props) => {
  const [rows, setRows] = useState<AmbienteExterno[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedGuids, setSelectedGuids] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const { temAcesso } = useAcesso();
  const originalValueRef = useRef<string>('');
  const canBlurRef = useRef<boolean>(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setSaveStatus?.('saving');
      try {
        const data = await ambienteExternoService.listarTodosPorTipo(props.tipo);
        setRows(data);
        setSaveStatus?.('saved');
        setTimeout(() => setSaveStatus?.('idle'), 3000);
      } catch (error) {
        console.error('Error fetching data:', error);
        toastService.error('Erro ao carregar indicadores');
        setSaveStatus?.('error');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [props.tipo]);

  const handleChange = async <K extends keyof AmbienteExterno>(
    index: number,
    field: K,
    value: AmbienteExterno[K],
    save: boolean = true
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;

    updated[index].pontuacao =
      updated[index].impacto *
      updated[index].probabilidade *
      updated[index].tendencia;

    if (save) {
      setSavingRow(index);
      setSaveStatus('saving');
      await salvar(updated[index]);
      setSavingRow(null);
    }

    setRows(updated);
  };

  const toggleSelect = (guid?: string) => {
    if (!guid) return;
    setSelectedGuids((prev) => (prev.includes(guid) ? prev.filter((g) => g !== guid) : [...prev, guid]));
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedGuids([]);
      setAllSelected(false);
    } else {
      const guids = rows.map((r) => r.guid).filter((g): g is string => !!g);
      setSelectedGuids(guids);
      setAllSelected(true);
    }
  };

  useEffect(() => {
    const selectableCount = rows.filter((r) => !!r.guid).length;
    setAllSelected(selectableCount > 0 && selectedGuids.length === selectableCount);
  }, [rows, selectedGuids]);

  const handleAddRow = async () => {
    const newRow: AmbienteExterno = {
      guid: undefined,
      tipo: props.tipo,
      opcao: '',
      fator: 'Oportunidade',
      impacto: 1,
      probabilidade: 1,
      tendencia: 1,
      pontuacao: 1,
      ordem: rows.length + 1,
      ativo: true,
    };

    const newRows = [...rows, newRow];
    setRows(newRows);

    const newIndex = newRows.length - 1;
    originalValueRef.current = '';
    canBlurRef.current = false;
    setEditingRow(newIndex);

    setTimeout(() => {
      canBlurRef.current = true;
      const el = rowRefs.current[newIndex];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };


  const handleDeleteRow = async (index: number) => {
    const rowToDelete = rows[index];
    const newRows = [...rows];
    newRows.splice(index, 1);
    try {
      setSaveStatus('saving');
      await ambienteExternoService.deletar(rowToDelete.guid!);
      setRows(newRows);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting row:', error);
      toastService.error('Erro ao excluir indicador');
      setSaveStatus('error');
    }
  };

  const handleDeleteAll = async () => {
    setOpenConfirmDialog(false);
    setSaveStatus('saving');
    try {
      const guidsToDelete = selectedGuids;
      if (guidsToDelete.length > 0) {
        await ambienteExternoService.deletarEmMassa(guidsToDelete);
      }

      const remainingRows = rows.filter((r) => !(r.guid && guidsToDelete.includes(r.guid)));
      setRows(remainingRows);
      setSelectedGuids([]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting all rows:', error);
      toastService.error('Erro ao excluir todos os indicadores selecionados');
      setSaveStatus('error');
    }
  };

  const salvar = async (row: AmbienteExterno) => {
    try {
      row.guid = (await ambienteExternoService.save(row)).guid;
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving row:', error);
      toastService.error('Erro ao salvar indicador');
      setSaveStatus('error');
    }
  };

  return (
    <Box>
      <ProtecaoBotao tela="ambiente-externo" tipo="alteracao">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2 }}>
              <Tooltip title="Adicionar um novo Ponto Forte">
                <PrimaryButton onClick={handleAddRow}>
                  + Adicionar
                </PrimaryButton>
              </Tooltip>
              <Tooltip title={selectedGuids.length > 0 ? 'Excluir' : 'Selecione ao menos um item'}>
                <div>
                  <PrimaryButton
                    variant="outlined"
                    color="error"
                    disabled={selectedGuids.length === 0}
                    onClick={() => setOpenConfirmDialog(true)}
                  >
                    Excluir
                  </PrimaryButton>
                </div>
              </Tooltip>
            </Box>
          <SaveStatusIndicator status={saveStatus} />
        </Box>
      </ProtecaoBotao>

      <Paper elevation={3} sx={{ mt: 2 }}>
        <TableContainer sx={{ borderRadius: '8px' }}>
          <Table aria-label="pestel table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center" sx={{ width: '40px', paddingLeft: '8px', paddingRight: '8px' }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedGuids.length > 0 && !allSelected}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'select all' }}
                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                  />
                </StyledTableCell>
                <StyledTableCell sx={{ width: '50%' }}><strong>Opção</strong></StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Fator"
                    description="
                      Oportunidades:
                      Fatores externos e positivos que favorecem o crescimento e os objetivos da empresa, permitindo antecipar tendências e gerar vantagens competitivas.
                      Ameaças:
                      Fatores externos e negativos que podem dificultar o desempenho; reconhecê-los ajuda a reduzir riscos e preparar estratégias defensivas.
                    "
                    placement="top"
                  >
                    <strong>Fator</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Impacto"
                    description="Quanto este indicador realmente tem impacto sobre o resultado da empresa."
                    placement="top"
                  >
                    <strong>Impacto</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Chance"
                    description="Quais as reais chances deste indicador, oportunidade ou ameaça, vir a acontecer na prática."
                    placement="top"
                  >
                    <strong>Chance</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Tendência"
                    description="Refere-se à evolução provável do indicador ao longo do tempo, caso nenhuma ação seja tomada. Analisa se o indicador tende a ampliar, permanecer estável ou até regredir."
                    placement="top"
                  >
                    <strong>Tendência</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center"><strong>Pontuação</strong></StyledTableCell>
                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LoadingIndicator message="Carregando indicadores..." />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">Nenhum indicador encontrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell align="center">
                      <Checkbox
                        checked={!!row.guid && selectedGuids.includes(row.guid)}
                        disabled={!row.guid || savingRow === index}
                        onChange={() => row.guid && toggleSelect(row.guid)}
                        inputProps={{ 'aria-label': `select row ${index}` }}
                      />
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {editingRow === index ? (
                        <ProtecaoBotao tela="ambiente-externo" tipo="alteracao">
                          <>
                            <TextField
                              fullWidth
                              multiline
                              minRows={1}
                              variant="standard"
                              inputRef={(input) => {
                                if (input) {
                                  setTimeout(() => {
                                    input.focus();
                                  }, 100);
                                }
                              }}
                              value={row.opcao}
                              onFocus={() => {
                                originalValueRef.current = row.opcao;
                                canBlurRef.current = false;
                                setTimeout(() => {
                                  canBlurRef.current = true;
                                }, 200);
                              }}
                              onChange={(e) => handleChange(index, 'opcao', e.target.value, false)}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (row.opcao.trim() !== '') {
                                    setSavingRow(index);
                                    await salvar(row);
                                    setSavingRow(null);
                                    setEditingRow(null);
                                  } else {
                                    toastService.warning("A opção não pode estar vazia.");
                                  }
                                }
                              }}
                              onBlur={async () => {
                                if (!canBlurRef.current || editingRow !== index) return;
                                
                                if (row.opcao.trim() !== '') {
                                  if (row.opcao !== originalValueRef.current) {
                                    setSavingRow(index);
                                    await salvar(row);
                                    setSavingRow(null);
                                  }
                                  setEditingRow(null);
                                } else {
                                  if (originalValueRef.current === '') {
                                    toastService.warning("A opção não pode estar vazia.");
                                    const newRows = [...rows];
                                    newRows.splice(index, 1);
                                    setRows(newRows);
                                  } else {
                                    handleChange(index, 'opcao', originalValueRef.current, false);
                                  }
                                  setEditingRow(null);
                                }
                              }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: 16,
                                  cursor: 'text',
                                  padding: '4px 0px',
                                },
                              }}
                            />
                            <Typography sx={{ color: 'gray', fontSize: '0.75rem', mt: 1 }}>
                              Pressione "Enter" ou clique fora para confirmar
                            </Typography>
                          </>
                        </ProtecaoBotao>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                          {row.opcao}
                        </Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {temAcesso('ambiente-externo', 'alteracao') ? (
                        <Select
                          value={row.fator}
                          onChange={(e) => handleChange(index, 'fator', e.target.value)}
                          size="small"
                          disabled={savingRow === index}
                        >
                          {CLASSIFICACOES_AMBIENTE_EXTERNO.map((value) => (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="body2">{row.fator}</Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {temAcesso('ambiente-externo', 'alteracao') ? (
                        <Select
                          value={row.impacto}
                          onChange={(e) => handleChange(index, 'impacto', parseInt(String(e.target.value)))}
                          size="small"
                          disabled={savingRow === index}
                          renderValue={(selected) => String(selected)}
                        >
                          {impactoOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="body2">{row.impacto}</Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {temAcesso('ambiente-externo', 'alteracao') ? (
                        <Select
                          value={row.probabilidade}
                          onChange={(e) => handleChange(index, 'probabilidade', parseInt(String(e.target.value)))}
                          size="small"
                          disabled={savingRow === index}
                          renderValue={(selected) => String(selected)}
                        >
                          {probabilidadeOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="body2">{row.probabilidade}</Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {temAcesso('ambiente-externo', 'alteracao') ? (
                        <Select
                          value={row.tendencia}
                          onChange={(e) => handleChange(index, 'tendencia', parseInt(String(e.target.value)))}
                          size="small"
                          disabled={savingRow === index}
                          renderValue={(selected) => String(selected)}
                        >
                          {tendenciaOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="body2">{row.tendencia}</Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {row.pontuacao}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      <ProtectedBasicMenu
                        tela='ambiente-externo'
                        onEdit={() => {
                          originalValueRef.current = row.opcao;
                          canBlurRef.current = false;
                          setEditingRow(index);
                        }}
                        onDelete={() => handleDeleteRow(index)}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ConfirmDeleteDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleDeleteAll}
        title="Confirmar Exclusão"
        message="Tem certeza de que deseja excluir os indicadores selecionados?"
        confirmButtonText="Confirmar"
      />
    </Box>
  );
};

export default TabelaPESTEL;
